/**
 * PATH: packages/drafting/src/canvas/useCanvasView.ts
 * FILE_ID: DRAFTING-CANVAS-USEVIEW-001
 * PURPOSE: Own the canvas view transform ({scale,x,y}) — fit-to-size on
 *   first paint and when the paper changes, wheel-zoom toward the cursor,
 *   and a manual fit(). Pan is applied externally (Layer drag -> setView).
 * WHY: Keeps DraftCanvas under the 300-line ceiling and isolates the
 *   zoom/fit maths (pure helpers live in geometry.ts) from rendering.
 */
import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { type Size, type View, fitView, zoomToPoint } from './geometry'

export interface UseCanvasView {
  view: View
  setView: (v: View) => void
  onWheelZoom: (deltaY: number, pointer: { x: number; y: number }) => void
  fit: () => void
}

export function useCanvasView(content: Size, viewport: Size): UseCanvasView {
  const [view, setView] = useState<View | null>(null)
  const lastPaper = useRef<string>('')

  useLayoutEffect(() => {
    if (viewport.w < 2 || viewport.h < 2) return
    const paperKey = `${content.w}x${content.h}`
    // Fit on first valid viewport, or whenever the paper size changes.
    // A plain viewport resize does NOT refit — it preserves the user's zoom.
    if (view === null || lastPaper.current !== paperKey) {
      setView(fitView(content, viewport))
      lastPaper.current = paperKey
    }
    // Depend on the primitive dims, not the {content,viewport} object
    // identities (those change every render → would refit on every paint).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.w, content.h, viewport.w, viewport.h, view])

  const onWheelZoom = useCallback(
    (deltaY: number, pointer: { x: number; y: number }) => {
      setView((v) => {
        const base = v ?? fitView(content, viewport)
        return zoomToPoint(base, pointer, deltaY < 0 ? 1.1 : 1 / 1.1)
      })
    },
    [content, viewport],
  )

  const fit = useCallback(() => setView(fitView(content, viewport)), [content, viewport])

  const setViewSafe = useCallback((v: View) => setView(v), [])

  return { view: view ?? fitView(content, viewport), setView: setViewSafe, onWheelZoom, fit }
}
